/**
 * CustomerComplaintsListPage — citizen view of complaints they have filed.
 *
 * Backed by `GET /v1/service-desk/cases-mine`, the citizen-scoped list
 * endpoint (RBAC: `Complaint/read-own`). The API returns a paged envelope
 * `{ items, total, page, pageSize }`; rendering is list-only because the
 * citizen-side single-case detail endpoint is not yet wired (the wrapper
 * in `lib/api/service-desk.ts` only exposes list + create), so each row
 * is non-clickable for now.
 */
import { useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, MessageSquare } from 'lucide-react';

import { listCitizenCases, type CaseResponse } from '../../lib/api/service-desk';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { PageHeader, StatusBadge } from './PortalUI';

const PRIORITY_COLORS: Readonly<Record<string, string>> = {
  LOW:      'bg-green-50 text-green-700 border-green-200',
  MEDIUM:   'bg-amber-50 text-amber-700 border-amber-200',
  HIGH:     'bg-orange-50 text-orange-700 border-orange-200',
  CRITICAL: 'bg-red-50 text-red-700 border-red-200',
};

const FORM_LABELS: Readonly<Record<string, string>> = {
  COMPLAINT_DELIVERY_DELAY: 'Delivery delay',
  COMPLAINT_DAMAGED_ITEM:   'Damaged item',
  COMPLAINT_LOST_ITEM:      'Lost item',
  COMPLAINT_WRONG_DELIVERY: 'Wrong delivery',
  COMPLAINT_STAFF_CONDUCT:  'Staff conduct',
  COMPLAINT_BILLING_ERROR:  'Billing error',
  COMPLAINT_OTHER:          'Other',
};

function formatFormKey(formKey: string): string {
  return FORM_LABELS[formKey] ?? formKey.replace(/^COMPLAINT_/, '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function formatCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function CustomerComplaintsListPage(): JSX.Element {
  const { token } = useCustomerAuth();

  const query = useQuery({
    queryKey: ['service-desk', 'cases-mine'],
    queryFn: () => listCitizenCases({ bearerToken: token ?? undefined }),
    enabled: token !== null,
  });

  const items = useMemo<readonly CaseResponse[]>(() => query.data?.items ?? [], [query.data]);

  return (
    <div>
      <PageHeader
        title="My Complaints"
        description="Track the complaints you have filed with the service desk."
        actions={
          <Link
            to="/portal/complaints/new"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors"
          >
            <MessageSquare className="w-4 h-4" /> File a complaint
          </Link>
        }
      />

      <ComplaintsList
        loading={query.isLoading}
        error={query.error}
        items={items}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComplaintsList — loading / error / empty / populated states.
// ---------------------------------------------------------------------------

interface ComplaintsListProps {
  readonly loading: boolean;
  readonly error: unknown;
  readonly items: readonly CaseResponse[];
}

function ComplaintsList({ loading, error, items }: ComplaintsListProps): JSX.Element {
  if (loading) {
    return (
      <div
        className="bg-white rounded-xl border border-border p-6"
        role="status"
        aria-live="polite"
        data-testid="complaints-loading"
      >
        <div className="space-y-3">
          {[0, 1, 2].map((k) => (
            <div
              key={k}
              className="h-16 rounded-lg bg-slate-100 animate-pulse"
              aria-hidden="true"
            />
          ))}
        </div>
        <p className="sr-only">Loading your complaints…</p>
      </div>
    );
  }

  if (error) {
    const msg = error instanceof Error ? error.message : 'Could not load your complaints.';
    return (
      <div
        className="bg-white rounded-xl border border-danger-light p-6"
        role="alert"
        data-testid="complaints-error"
      >
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-danger mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-danger mb-1">
              We couldn&apos;t load your complaints.
            </p>
            <p className="text-xs text-muted">{msg}</p>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="bg-white rounded-xl border border-border p-12 text-center"
        data-testid="complaints-empty"
      >
        <MessageSquare className="w-10 h-10 text-muted mx-auto mb-3" />
        <p className="text-sm font-semibold text-gray-700 mb-1">
          You have not filed any complaints yet.
        </p>
        <p className="text-xs text-muted mb-4">
          If something has gone wrong with a parcel or service, let us know.
        </p>
        <Link
          to="/portal/complaints/new"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors"
        >
          <MessageSquare className="w-4 h-4" /> File a complaint
        </Link>
      </div>
    );
  }

  return (
    <ul
      className="bg-white rounded-xl border border-border divide-y divide-border overflow-hidden"
      data-testid="complaints-list"
    >
      {items.map((item) => (
        <ComplaintRow key={item.id} item={item} />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// ComplaintRow
// ---------------------------------------------------------------------------

function ComplaintRow({ item }: { readonly item: CaseResponse }): JSX.Element {
  const priorityClass = PRIORITY_COLORS[item.priority] ?? 'bg-gray-50 text-gray-700 border-gray-200';

  return (
    <li
      className="p-4"
      data-testid="complaint-row"
      data-case-number={item.caseNumber}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusBadge status={item.status} />
            <span
              className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-full border ${priorityClass}`}
            >
              {item.priority}
            </span>
          </div>
          <p
            className="font-mono text-sm font-semibold text-nipost-green"
            data-testid="complaint-case-number"
          >
            {item.caseNumber}
          </p>
          <p className="text-xs text-gray-700 mt-1 truncate">
            <span className="font-medium">{formatFormKey(item.formKey)}</span>
            <span className="text-muted mx-1.5">·</span>
            <span>{item.title}</span>
          </p>
          <p className="text-[11px] text-muted mt-0.5">
            Filed {formatCreatedAt(item.createdAt)}
          </p>
        </div>
      </div>
    </li>
  );
}

